/* eslint-disable react/sort-comp */
import React from 'react';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { applicationRouteConstants } from '../../shared/_constants';
import { msalConfig, loginRequest, tokenRequest } from './AuthConfig';
import { setAuth } from './AuthActions';
import { showLoader } from '../../modules/Hub/Actions/HubActions';

// const decoder = require('jwt-decode');

// Create MSAL instance - MUST be created before any redirect happens
const msalInstance = new PublicClientApplication(msalConfig);

// Track initialization state
let isInitializing = false;
let isInitialized = false;
let initializationPromise = null;

// Helper function to ensure MSAL is initialized
export async function ensureMsalInitialized() {
  if (isInitialized) {
    return true;
  }

  if (isInitializing && initializationPromise) {
    await initializationPromise;
    return true;
  }

  if (!isInitializing) {
    isInitializing = true;
    initializationPromise = msalInstance
      .initialize()
      .then(() => {
        isInitialized = true;
        isInitializing = false;
        initializationPromise = null;
        return true;
      })
      .catch((error) => {
        console.error('MSAL initialization error:', error);
        isInitializing = false;
        initializationPromise = null;
        throw error;
      });
    return initializationPromise;
  }

  return false;
}

// CRITICAL: Process redirect immediately on page load, before React renders
// This ensures the hash is processed before React Router can interfere
let redirectProcessed = false;
const processRedirectImmediately = async () => {
  if (redirectProcessed) return;

  // Check if we have auth code in URL
  const hasCode =
    window.location.hash.includes('code=') ||
    window.location.search.includes('code=');
  if (!hasCode) {
    redirectProcessed = true;
    return;
  }

  console.log('IMMEDIATE REDIRECT PROCESSING - START');
  console.log('URL:', window.location.href);
  console.log('Hash:', window.location.hash.substring(0, 100));

  // Check MSAL state in localStorage before processing
  const msalStateKeys = Object.keys(localStorage).filter(
    (key) =>
      key.startsWith('msal.') &&
      (key.includes('state') || key.includes('interaction'))
  );
  console.log('MSAL state keys before processing:', msalStateKeys);

  try {
    // Initialize MSAL if not already initialized
    console.log(
      'Ensuring MSAL is initialized for immediate redirect processing...'
    );
    await ensureMsalInitialized();
    console.log('MSAL initialized');

    // Process redirect immediately
    console.log('Calling handleRedirectPromise immediately...');
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      console.log(
        'Redirect processed immediately:',
        response.account?.username
      );
      // Store response to be handled by component
      window.__MSAL_REDIRECT_RESPONSE__ = response;
    } else {
      console.log(' No response from immediate handleRedirectPromise');
      // Check state keys again
      const msalStateKeysAfter = Object.keys(localStorage).filter(
        (key) =>
          key.startsWith('msal.') &&
          (key.includes('state') || key.includes('interaction'))
      );
      console.log('MSAL state keys after processing:', msalStateKeysAfter);
    }
    redirectProcessed = true;
  } catch (error) {
    console.error('Immediate redirect processing error:', error);
    console.error('Error code:', error.errorCode);
    console.error('Error message:', error.message);
    redirectProcessed = true;
  }
  console.log('IMMEDIATE REDIRECT PROCESSING - END');
};

// Process redirect immediately when module loads (before React)
if (typeof window !== 'undefined') {
  // Use setTimeout to ensure DOM is ready but before React renders
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processRedirectImmediately);
  } else {
    // DOM already loaded, process immediately
    processRedirectImmediately();
  }
}

/**
 * AuthProvider HOC component that exposes auth functions to child components.
 * Usage: import withAuthProvider and wrap component to get the auth functions when exporting
 * like: withAuthProvider(<your component>);
 */
function withAuthProvider(WrappedComponent) {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        error: null,
        isInitialized: false,
      };
      this.msalInstance = msalInstance;
    }

    async componentDidMount() {
      console.log('AuthProvider componentDidMount - START');
      console.log('Current URL:', window.location.href);
      console.log('Current pathname:', window.location.pathname);
      console.log('Current hash:', window.location.hash);
      console.log('Current search:', window.location.search);
      console.log('Location props:', this.props.location);
      console.log('Navigate prop exists:', !!this.props.navigate);

      window.addEventListener('storage', this.storageChange.bind(this));

      // Check if redirect was already processed
      const preProcessedResponse = window.__MSAL_REDIRECT_RESPONSE__;
      if (preProcessedResponse) {
        console.log('Found pre-processed redirect response');
        delete window.__MSAL_REDIRECT_RESPONSE__;
        this.handleAuthResponse(preProcessedResponse);
        return;
      }

      // Check if we have auth code in URL hash or search
      const hasCodeInHash = window.location.hash.includes('code=');
      const hasCodeInSearch = window.location.search.includes('code=');
      const hasAuthParams = hasCodeInHash || hasCodeInSearch;
      console.log('Has code in hash:', hasCodeInHash);
      console.log('Has code in search:', hasCodeInSearch);
      console.log('Has auth params:', hasAuthParams);

      // Wait for MSAL to be fully initialized (only initialize once)
      try {
        console.log('Ensuring MSAL is initialized...');
        await ensureMsalInitialized();
        console.log('MSAL initialized successfully');
        this.setState({ isInitialized: true });

        // Handle redirect response if any (must be called on every page load for loginRedirect)
        console.log('Calling handleRedirectPromise...');
        console.log('Redirect URI in config:', msalConfig.auth.redirectUri);
        console.log('Current window location:', window.location.href);
        console.log(
          'Expected redirect URI:',
          `${window.location.origin}${applicationRouteConstants.HUB}`
        );

        // Check MSAL cache for any stored state
        const msalCacheKeys = Object.keys(localStorage).filter((key) =>
          key.startsWith('msal.')
        );
        console.log('MSAL cache keys:', msalCacheKeys.length);
        if (msalCacheKeys.length > 0) {
          console.log('MSAL cache keys:', msalCacheKeys.slice(0, 5)); // Log first 5
        }

        let response;
        try {
          response = await this.msalInstance.handleRedirectPromise();
          console.log('handleRedirectPromise result:', response);
        } catch (redirectError) {
          console.error('handleRedirectPromise error:', redirectError);
          console.error('Error code:', redirectError.errorCode);
          console.error('Error message:', redirectError.message);
          response = null;
        }

        // If no response but we have auth params in URL, try calling it again
        // Sometimes MSAL needs a moment to process the hash
        if (!response && hasAuthParams) {
          console.log(
            'No response but auth params present, waiting and retrying...'
          );
          await new Promise((resolve) => setTimeout(resolve, 500));
          try {
            response = await this.msalInstance.handleRedirectPromise();
            console.log('handleRedirectPromise retry result:', response);
          } catch (retryError) {
            console.error('handleRedirectPromise retry error:', retryError);
            response = null;
          }

          // If still no response, check if there's an error in MSAL
          if (!response) {
            console.log(
              'Still no response after retry. Checking for errors...'
            );
            // Try to manually check if we can get accounts
            const accounts = this.msalInstance.getAllAccounts();
            console.log('Accounts after failed redirect:', accounts.length);
            console.log(
              'This might indicate a redirect URI mismatch or state mismatch'
            );
            console.log(
              'Check that Azure AD redirect URI matches:',
              msalConfig.auth.redirectUri
            );
          }
        }

        if (response) {
          console.log('Redirect response received:', {
            account: response.account?.username,
            hasAccessToken: !!response.accessToken,
            scopes: response.scopes,
          });
          this.handleAuthResponse(response);
        } else {
          console.log('No redirect response, checking existing accounts...');
          // Check if user is already logged in (for page refreshes)
          const accounts = this.msalInstance.getAllAccounts();
          console.log('Existing accounts:', accounts.length);
          if (accounts.length > 0) {
            const account = accounts[0];
            console.log('Found existing account:', account.username);
            this.props.setAuth({
              Authenticated: true,
              Authorised: true,
              user: account.username,
              error: null,
            });
          } else {
            console.log('No existing accounts found');
            // If we have auth params but no response and no accounts, there might be an issue
            if (hasAuthParams) {
              console.log(
                'WARNING: Auth params in URL but no response and no accounts!'
              );
              console.log('This might indicate a redirect processing issue');
            }
          }
        }
      } catch (error) {
        console.error('MSAL initialization error:', error);
        console.error('Error code:', error.errorCode);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        this.setState({ isInitialized: true });
        // Handle redirect errors
        if (
          error.errorCode === 'user_cancelled' ||
          error.errorCode === 'consent_required'
        ) {
          console.log('User cancelled or consent required');
          this.props.setAuth({
            Authenticated: null,
            Authorised: null,
            user: null,
          });
        }
      }
      console.log('AuthProvider componentDidMount - END');
    }

    handleAuthResponse(response) {
      console.log('handleAuthResponse - START');
      console.log('Response:', response);

      if (response && response.account) {
        console.log(
          'Processing auth response, setting account:',
          response.account.username
        );
        // Set active account
        this.msalInstance.setActiveAccount(response.account);
        console.log('Active account set');

        // Set auth state immediately
        console.log('Setting Redux auth state...');
        this.props.setAuth({
          Authenticated: true,
          Authorised: true,
          user: response.account.username,
          error: null,
        });
        console.log('Redux auth state set');

        // Check if we're already at the HUB route (Azure redirects there)
        const { navigate, location } = this.props;
        const currentPath = location?.pathname || window.location.pathname;
        const hubPath = applicationRouteConstants.HUB;

        console.log('Current path:', currentPath);
        console.log('Hub path:', hubPath);
        console.log('Navigate prop:', !!navigate);
        console.log('Location prop:', location);

        // Normalize paths for comparison (remove trailing slashes)
        const normalizedCurrent = currentPath.replace(/\/$/, '');
        const normalizedHub = hubPath.replace(/\/$/, '');

        console.log('Normalized current:', normalizedCurrent);
        console.log('Normalized hub:', normalizedHub);
        console.log('Paths match:', normalizedCurrent === normalizedHub);

        // If we're already at HUB, update location state immediately for PrivateRoute
        if (normalizedCurrent === normalizedHub) {
          console.log('Already at HUB route, updating location state');
          if (navigate) {
            console.log('Calling navigate with replace and state');
            // Update location state immediately so PrivateRoute allows access
            navigate(hubPath, {
              replace: true,
              state: { isAuthenticated: true },
            });
            console.log('Navigate called successfully');
          } else {
            console.log('Navigate prop not available');
          }
        } else {
          // If we're not at HUB, navigate there
          console.log('Not at HUB route, will navigate to HUB');
          // Use setTimeout to ensure Redux state is set before navigation
          setTimeout(() => {
            console.log('Timeout completed, calling authRedirect');
            this.authRedirect();
          }, 100);
        }
      } else {
        console.log('handleAuthResponse - No account in response');
      }
      console.log('handleAuthResponse - END');
    }

    /**
     * Fired when storage change event is fired
     * @param event
     */
    storageChange(event) {
      const accounts = this.msalInstance.getAllAccounts();
      if (
        event.storageArea &&
        localStorage.getItem('isLoggedOut') &&
        accounts.length === 0
      ) {
        localStorage.removeItem('isLoggedOut');
        this.logout();
      }
    }

    /**
     * On component unmount
     */
    componentWillUnmount() {
      window.removeEventListener('storage', this.storageChange.bind(this));
    }

    /**
     * Check for token expiration for every 30 minutes
     * If the current date and time is greater than token expiration time
     * then logout the application
     */
    checkTokenExpiration() {
      setInterval(() => {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          if (
            account.idTokenClaims &&
            account.idTokenClaims.exp < Date.now() / 1000
          ) {
            this.logout();
          }
        }
      }, 1800000);
    }

    render() {
      const { error } = this.state;
      this.checkTokenExpiration();
      return (
        <WrappedComponent
          error={error}
          login={(loginHint) => this.login(loginHint)}
          logout={() => this.logout()}
          getToken={(scopes, hint) => this.getToken(scopes, hint)}
          setError={(message, debug) => this.setErrorMessage(message, debug)}
          {...this.props}
          {...this.state}
        />
      );
    }

    login = async (loginHint) => {
      console.log('login method - START');
      console.log('Login hint:', loginHint);
      try {
        // Clear any existing errors
        this.setState({ error: null });

        // For kiosk app, clear any cached accounts to force fresh login
        const accounts = this.msalInstance.getAllAccounts();
        console.log('Existing accounts before clear:', accounts.length);
        if (accounts.length > 0) {
          console.log('Clearing cached accounts for fresh authentication');
          this.msalInstance.setActiveAccount(null);
        }

        // Login via redirect (doesn't return immediately - response handled in componentDidMount)
        const loginRequestConfig = {
          ...loginRequest,
          prompt: 'login', // Force fresh authentication
        };

        if (loginHint) {
          loginRequestConfig.loginHint = loginHint;
        }

        console.log(
          'Initiating login redirect with request:',
          loginRequestConfig
        );
        console.log('Redirect URI will be:', msalConfig.auth.redirectUri);
        await this.msalInstance.loginRedirect(loginRequestConfig);
        console.log('loginRedirect called - page will redirect to Azure');

        // Note: loginRedirect doesn't return a response here
        // The response will be handled by handleRedirectPromise in componentDidMount
        // when the user returns from the Azure login page
      } catch (err) {
        console.error('Login error:', err);
        console.error('Error code:', err.errorCode);
        console.error('Error message:', err.message);
        const normalizedError = this.normalizeError(err);
        this.setState({ error: normalizedError });
        this.props.setAuth({
          Authenticated: null,
          Authorised: null,
          user: null,
          error: normalizedError,
        });
      }
      console.log('login method - END');
    };

    /**
     * On logout close all office 365 application tabs and fire msal logout
     */
    logout() {
      this.props.openedTabList.map((tab) => {
        return tab.close();
      });
      localStorage.setItem('isLoggedOut', true);
      localStorage.removeItem('i18nextLng');

      // Clear active account
      this.msalInstance.setActiveAccount(null);

      // Update Redux state
      this.props.setAuth({
        Authenticated: false,
        Authorised: false,
        user: null,
        error: null,
      });

      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
        });
      } else {
        // If no accounts, just redirect to login
        const { navigate } = this.props;
        if (navigate) {
          navigate(applicationRouteConstants.DEFAULT);
        } else {
          window.location.href = applicationRouteConstants.DEFAULT;
        }
      }
    }

    async getToken(scopes, hint) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }

      const account = accounts[0];
      const request = {
        scopes: scopes || tokenRequest.scopes,
        account: account,
        loginHint: hint,
      };

      try {
        // Try to get token silently first
        const silentResult =
          await this.msalInstance.acquireTokenSilent(request);
        return silentResult.accessToken;
      } catch (err) {
        // If silent request fails, try interactive
        if (err instanceof InteractionRequiredAuthError) {
          const interactiveResult =
            await this.msalInstance.acquireTokenPopup(request);
          return interactiveResult.accessToken;
        }
        throw err;
      }
    }

    setErrorMessage(message, debug) {
      this.setState({
        error: { message, debug },
      });
    }

    authRedirect() {
      console.log('authRedirect - START');
      const { history, navigate } = this.props;
      const hubPath = applicationRouteConstants.HUB;

      console.log('Hub path:', hubPath);
      console.log('Navigate prop:', !!navigate);
      console.log('History prop:', !!history);
      console.log('Current Redux auth state:', this.props.isAuthorised);

      // Support both react-router v5 (history) and v6 (navigate)
      if (navigate) {
        console.log('Using navigate to redirect to:', hubPath);
        try {
          navigate(hubPath, {
            replace: true,
            state: { isAuthenticated: true },
          });
          console.log('Navigate called successfully');
        } catch (err) {
          console.error('Navigate error:', err);
        }
      } else if (history && history.push) {
        console.log('Using history.push to redirect to:', hubPath);
        try {
          history.push(hubPath, { isAuthenticated: true });
          console.log('History.push called successfully');
        } catch (err) {
          console.error('History.push error:', err);
        }
      } else {
        // Fallback: direct navigation
        console.log('Using window.location to redirect to:', hubPath);
        window.location.href = hubPath;
      }
      console.log('authRedirect - END');
    }

    normalizeError(error) {
      let normalizedError = {};
      if (typeof error === 'string') {
        const errParts = error.split('|');
        normalizedError =
          errParts.length > 1
            ? { message: errParts[1], debug: errParts[0] }
            : { message: error };
      } else {
        normalizedError = {
          message: error.message,
          debug: JSON.stringify(error),
        };
      }
      return normalizedError;
    }

    isInteractionRequired(error) {
      if (!error.message || error.message.length <= 0) {
        return false;
      }

      return (
        error.message.indexOf('consent_required') > -1 ||
        error.message.indexOf('interaction_required') > -1 ||
        error.message.indexOf('login_required') > -1
      );
    }
  };
}

function mapDispatchToProps(dispatch) {
  return {
    showLoader: (isLoader) => {
      dispatch(showLoader(isLoader));
    },
    setAuth: (status) => {
      dispatch(setAuth(status));
    },
  };
}

const mapStateToProps = (state, props) => {
  return {
    openedTabList: state.HubReducer.openedTabList,
    isAuthorised: state.AuthReducer.isAuthorised,
    user: state.AuthReducer.user,
  };
};

const composeWrapper = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withAuthProvider
);

// Export the MSAL instance for use in other parts of the app
export { msalInstance };
export default composeWrapper;
